'use client';
import {
  Box,
  BoxProps,
  Button,
  CircularProgress,
  Paper,
  PaperProps,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { differenceInCalendarDays } from 'date-fns';
import React, { ElementType, FC, useMemo, useRef, useState } from 'react';

import {
  AbgTypography,
  mergeSx,
  ProgressBarStepId,
  useAemLayoutContext,
} from '@abg/kit-base';
import { VehicleAvailabilityEnum } from '@abg/shared/constants';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { useIsUpgradeFlow } from '../../../lib/hooks/use-is-upgrade-flow';
import { useAncillariesDialogContext } from '../../organisms/Ancillaries/contexts/AncillariesDialogContext/AncillariesDialogContext';
import TermsAndConditionsLink from '../../organisms/VehicleList/organisms/VehicleListItem/TermsAndConditionsLink';
import { useAnalyticsUpdatePageReference } from '../../shared/analytics/hooks';
import { useTranslationContext } from '../../shared/context';
import { AncillariesI18nKeys } from '../../shared/types';
import { bookingFlowStoreSelector } from '../../store';
import { useTncTermsStore } from '../../store/tnc-terms';
import { CarDetailsPopover } from '../CarDetailsPopover';
import { CarInfoAccordion } from '../CarInfoAccordion';
import { AccordionPanelImageStyle } from '../CarInfoAccordion/car-info-accordion.constants';
import { greenTextPoints } from '../CarInfoAccordion/components/CarPointsSummary/points-accordion.styling';

import { TotalAmount } from './components/TotalAmount';
import { WarningText } from './components/WarningText';
import { useIsCaliforniaPickup } from './hooks/use-is-california-pickup';
import { getWarningText } from './action-footer.helpers';
import { ActionFooterProps } from './action-footer.types';
import {
  actionButtonStyling,
  actionContainerStyling,
  buttonsContainerStyling,
  checkoutMobileActionButtonStyling,
  checkoutMobileWrapperStyling,
  footerWrapperStyling,
  loaderStyling,
  mobileDisclaimerBelowCtaStyling,
  tncContainerStyling,
} from './ActionFooter.styling';

// eslint-disable-next-line complexity
export const ActionFooter: FC<ActionFooterProps> = ({
  styleParts,
  onAction,
  onCostRemove,
  onPopOver,
  onSkipToReview,
  selectedItem,
  isActionDisabled = false,
  isSkipActionDisabled = false,
  showSkipToReview = false,
  caretStyles,
  recalculatePrices,
  pricingData,
  nextStepButtonLabel,
  isPricingDataLoading = false,
  isCheckoutMobile,
  isPayNow,
  enableQantasFrequentFlyerBanner,
}) => {
  const [isPopOverOpen, setIsPopOverOpen] = useState(false);
  const toggleReference = useRef<HTMLElement>(null);
  const { t, i18nKeys } = useTranslationContext<AncillariesI18nKeys>();
  const { userFlow, activeStep } = useAemLayoutContext();
  const { updateAnalyticsPageReference } = useAnalyticsUpdatePageReference();
  const pageContext = usePageContext();
  const isEmea = pageContext?.region === 'emea';
  const removeDisclaimer =
    pageContext?.features?.['feature.removeDisclaimer'] ?? false;
  const isModificationFlow = userFlow === 'modification';
  const { isUpgradeFlow } = useIsUpgradeFlow();

  const isCaliforniaPickup = useIsCaliforniaPickup();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const showDisclaimer = !removeDisclaimer && isCaliforniaPickup;
  const showDisclaimerBelowCta =
    showDisclaimer && (isMobile || isCheckoutMobile);

  const tncI18n = useTncTermsStore((state) => state.i18n);
  const vehicleAvailability =
    bookingFlowStoreSelector.use.vehicleAvailability?.();
  const pickupDatetime = bookingFlowStoreSelector.use.pickupDatetime();
  const returnDatetime = bookingFlowStoreSelector.use.returnDatetime();
  const vehicleImage = bookingFlowStoreSelector.use.vehicleImage();
  const vehicleType = bookingFlowStoreSelector.use.vehicleType();

  const isOnRequestVehicle =
    (vehicleAvailability as VehicleAvailabilityEnum) ===
    VehicleAvailabilityEnum.ON_REQUEST;

  const isLastStep = activeStep?.id === ProgressBarStepId.Checkout;

  const duration = useMemo(() => {
    if (!pickupDatetime || !returnDatetime) return '';

    const daysQty = differenceInCalendarDays(
      new Date(returnDatetime),
      new Date(pickupDatetime),
    );

    return `(${daysQty} ${t(i18nKeys[daysQty === 1 ? 'dayLabel' : 'days'])})`;
  }, [pickupDatetime, returnDatetime, t, i18nKeys]);

  const warningIfDisabled = t(i18nKeys.bottomPanelWarningLabel);

  const warningText = getWarningText({
    isActionDisabled,
    warning: selectedItem?.warning,
    warningIfDisabled,
  });

  const onPopOverClick = (isOpen: boolean) => {
    setIsPopOverOpen(isOpen);
    onPopOver?.(isOpen);
  };
  const dialogContextApi = useAncillariesDialogContext();

  type WrapperProps = BoxProps & PaperProps;
  const WrapperComponent: ElementType<WrapperProps> = isCheckoutMobile
    ? Paper
    : Box;

  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Manually update page reference for analytics in Confirmation page
    if (isLastStep) {
      updateAnalyticsPageReference();
    }

    // Call the original onAction handler
    onAction?.(event);
  };

  const baseActionButtonSx = mergeSx(
    actionButtonStyling,
    styleParts?.actionButton,
  );
  const upgradeActionButtonSx = isUpgradeFlow
    ? mergeSx(baseActionButtonSx, { '&&': { padding: '17px 18px' } })
    : baseActionButtonSx;
  const actionButtonSx = isCheckoutMobile
    ? mergeSx(upgradeActionButtonSx, checkoutMobileActionButtonStyling)
    : upgradeActionButtonSx;

  return (
    <WrapperComponent
      data-testid="ancillaries-action-footer"
      sx={
        isCheckoutMobile
          ? mergeSx(checkoutMobileWrapperStyling, styleParts?.footerWrapper)
          : footerWrapperStyling
      }
    >
      <Box data-testid="action-footer-warning-text">
        {warningText && dialogContextApi?.showBottomPanelWarning && (
          <WarningText text={warningText} />
        )}
      </Box>

      {isEmea && (
        <Box sx={tncContainerStyling}>
          <TermsAndConditionsLink i18n={tncI18n} />
        </Box>
      )}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent={isCheckoutMobile ? 'space-between' : undefined}
        flexWrap={isCheckoutMobile ? 'nowrap' : undefined}
        spacing={isCheckoutMobile ? 1 : undefined}
        sx={
          isCheckoutMobile
            ? { width: '100%' }
            : actionContainerStyling(isModificationFlow, isUpgradeFlow)
        }
      >
        {isPricingDataLoading && (
          <CircularProgress sx={loaderStyling} size={24} />
        )}

        {pricingData?.totals?.totalPoints &&
        pricingData?.totals?.totalPoints > 0 ? (
          <AbgTypography
            variant="bodySmallMedium"
            component={'div'}
            sx={{
              ...greenTextPoints,
              marginRight: { sm: '24px', xs: '10px' },
              maxWidth: { xs: '140px', sm: 'none' },
            }}
          >
            {t(i18nKeys.appliedPreferredPointsHeader)}
            {'  '}
            {'(' +
              (pricingData?.totals?.totalPoints &&
                pricingData?.totals?.totalPoints.toLocaleString()) +
              t(i18nKeys.appliedPointsText) +
              ')'}
          </AbgTypography>
        ) : null}

        {pricingData && (
          <TotalAmount
            totalText={t(i18nKeys.totalLabel)}
            amount={pricingData?.totals?.total}
            currencyCode={pricingData?.currencyCode}
            styleParts={styleParts}
            onPopOverClick={onPopOverClick}
            isPopOverOpen={isPopOverOpen}
            caretStyles={caretStyles}
            isCheckoutMobile={isCheckoutMobile}
            isModificationFlow={isModificationFlow}
            toggleRef={toggleReference}
            {...(showDisclaimer &&
              !showDisclaimerBelowCta && {
                priceDisclaimerLabel: t(i18nKeys.priceDisclaimerLabel),
              })}
          />
        )}

        <Stack
          direction="row"
          alignItems="center"
          sx={
            isCheckoutMobile
              ? { flexShrink: 0, ml: 'auto' }
              : buttonsContainerStyling(isModificationFlow)
          }
        >
          {showSkipToReview && (
            <Button
              sx={actionButtonStyling}
              variant="contained"
              color="primary"
              className="secondary"
              disabled={isSkipActionDisabled}
              onClick={onSkipToReview}
            >
              {t(i18nKeys.skipToReviewLabel)}
            </Button>
          )}
          <Button
            data-testid="action-footer-cta-button"
            sx={actionButtonSx}
            disabled={isActionDisabled || isPricingDataLoading}
            variant="contained"
            className="primary"
            onClick={handleActionClick}
            type="submit"
          >
            {isLastStep && isOnRequestVehicle && !isPayNow
              ? t(
                  'reservationCheckout.bookingOnRequest.confirmReservationRequest.label',
                )
              : t(
                  i18nKeys.continueBtnLabel,
                  nextStepButtonLabel
                    ? { nextStep: nextStepButtonLabel }
                    : undefined,
                )}
          </Button>
        </Stack>
      </Stack>

      {showDisclaimerBelowCta && (
        <Box width="100%">
          <AbgTypography
            sx={mobileDisclaimerBelowCtaStyling}
            data-testid="action-footer-mobile-disclaimer"
          >
            {t(i18nKeys.priceDisclaimerLabel)}
          </AbgTypography>
        </Box>
      )}

      {isPopOverOpen && (
        <CarDetailsPopover
          onPopOverClick={onPopOverClick}
          toggleRef={toggleReference}
        >
          <CarInfoAccordion
            onCostRemove={onCostRemove}
            isFromFooterPopover={true}
            duration={duration}
            vehicleImage={vehicleImage}
            vehicleType={vehicleType}
            pricingData={pricingData}
            currency={pricingData?.currencyCode}
            styleParts={styleParts?.popOver}
            recalculatePrices={recalculatePrices}
            accordionPanelImageStyle={AccordionPanelImageStyle}
            enableQantasFrequentFlyerBanner={enableQantasFrequentFlyerBanner}
          />
        </CarDetailsPopover>
      )}
    </WrapperComponent>
  );
};
